// ── Blueprints de thème par type de commerce ─────────────────────────
// Source unique pour :
//  • le sélecteur de blueprint dans l'onglet IA de l'éditeur de thème
//  • le prompt IA (structure recommandée → vrais thèmes cohérents)
//
// Un blueprint décrit la STRUCTURE (sections dans l'ordre), les balises
// de template à utiliser et la navigation attendue. L'IA s'en sert comme
// squelette pour produire un thème complet et pertinent.

export type BlueprintKind = 'simple' | 'marketplace';

export interface ThemeBlueprint {
  id: string;
  /** Nom du type de commerce */
  label: string;
  icon: string;
  /** À qui / quand ce type de site correspond */
  description: string;
  /** Type de compte Zifek correspondant (info) */
  typecompte?: number;
  kind: BlueprintKind;
  /** Sections recommandées, dans l'ordre */
  structure: string[];
  /** Balises de template à privilégier */
  tags: string[];
  /** Navigation attendue (règle : logo + Accueil + Nos produits / Nos services) */
  nav: string[];
  /** Directives de design par défaut proposées à l'IA */
  defaultInstructions: string;
}

export const THEME_BLUEPRINTS: ThemeBlueprint[] = [
  {
    id: 'boutique',
    label: 'Boutique de produits',
    icon: 'ri-shopping-bag-3-line',
    description: 'Vêtements, épicerie, cosmétiques, électronique, artisanat… priorité à la vente en ligne.',
    typecompte: 3,
    kind: 'simple',
    structure: [
      'Header (logo + navigation + recherche + panier)',
      'Hero : grande image, slogan, phrase courte, bouton « Voir les produits »',
      'Catégories principales (icônes + noms)',
      'Produits mis en avant (image, nom, prix, promo, ajouter au panier)',
      'Nouveautés (derniers produits)',
      'Meilleures ventes / produits populaires',
      'Bannière promotionnelle (code promo, offre limitée)',
      'Présentation de la boutique (qui sommes-nous)',
      'Avantages (livraison, paiement, garantie, service client)',
      'Avis clients',
      'Footer (contact, adresse, téléphone, conditions, réseaux)',
    ],
    tags: ['products', 'featured_products', 'testimonials', 'stats', 'contact_form'],
    nav: ['Accueil', 'Nos produits'],
    defaultInstructions: 'Boutique e-commerce claire et désirable. Grille produits généreuse, prix mis en avant, panier visible. Palette chaleureuse, jamais de bleu générique.',
  },
  {
    id: 'services',
    label: 'Prestation de services',
    icon: 'ri-service-line',
    description: 'Coiffeur, couturier, réparateur, consultant, photographe, mécanicien, agence… la prise de rendez-vous est prioritaire.',
    typecompte: 4,
    kind: 'simple',
    structure: [
      'Header (logo + navigation + bouton « Prendre rendez-vous »)',
      'Hero : photo pro, nom de l’entreprise, phrase, bouton rendez-vous + bouton services',
      'Services principaux (image/icône, nom, description, prix, durée, bouton Réserver)',
      'Service vedette (présentation détaillée, prix, durée, CTA réservation)',
      'Pourquoi nous choisir (expérience, qualité, rapidité, disponibilité)',
      'Réalisations / Portfolio (photos, avant/après)',
      'Avis clients',
      'Équipe (professionnels, spécialités)',
      'Prise de rendez-vous (calendrier, choix du service, date, heure)',
      'Localisation (adresse, carte, horaires) + contact rapide (tél, WhatsApp, email)',
      'Footer',
    ],
    tags: ['services', 'booking_form', 'portfolio', 'team', 'testimonials', 'contact_form', 'faq'],
    nav: ['Accueil', 'Nos services'],
    defaultInstructions: 'Site de prestation mettant en avant le rendez-vous. CTA réservation omniprésent, preuve sociale forte, ton professionnel et rassurant.',
  },
  {
    id: 'hotellerie',
    label: 'Hôtellerie / hébergement',
    icon: 'ri-hotel-line',
    description: 'Hôtel, riad, maison d’hôtes, auberge, résidence touristique. Objectif : présenter l’établissement et obtenir une réservation.',
    kind: 'simple',
    structure: [
      'Header (logo + Chambres + Services + Galerie + Expériences + À propos + Contact + Réserver)',
      'Hero plein écran (grande photo/vidéo, nom de l’établissement, localisation, description, bouton Réserver)',
      'Moteur de réservation (arrivée, départ, nombre de personnes, chambres, bouton Rechercher)',
      'Présentation de l’établissement (description, histoire, localisation)',
      'Chambres / hébergements (photos, nom, capacité, équipements, prix, Réserver)',
      'Services et équipements (piscine, Wi-Fi, restaurant, parking, spa, petit déjeuner…)',
      'Galerie photos (chambres, extérieur, restaurant, piscine, environnement)',
      'Expériences (excursions, visites, activités, gastronomie)',
      'Restaurant / gastronomie (menu, photos, horaires)',
      'Avis clients',
      'Localisation (carte, distance des lieux touristiques, adresse) + contact',
      'Footer',
    ],
    tags: ['booking_form', 'gallery', 'services', 'testimonials', 'stats', 'contact_form'],
    nav: ['Accueil', 'Chambres', 'Services', 'Galerie', 'Contact'],
    defaultInstructions: 'Design hôtelier premium et immersif. Grandes images plein écran, typographie élégante (serif display), palette sobre et luxueuse, réservation toujours accessible.',
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'ri-store-2-line',
    description: 'Produits + services + annonces de plusieurs vendeurs. La page d’accueil représente la place de marché, pas un seul commerçant.',
    typecompte: 1,
    kind: 'marketplace',
    structure: [
      'Header marketplace (logo Zifek, recherche globale, catégories, produits, services, boutiques, compte, Publier)',
      'Grande recherche (Que recherchez-vous ? + Ville + Catégorie + Rechercher)',
      'Catégories principales (Mode, Maison, Électronique, Alimentation, Beauté, Services, Immobilier, Automobile, Emploi…)',
      'Produits populaires (image, nom, note, prix, ville, boutique, Voir)',
      'Services populaires (image, service, prix, ville, prestataire, Réserver)',
      'Dernières annonces (image, titre, prix, ville, ancienneté)',
      'Boutiques à découvrir (logo, nom, catégorie, ville, nb produits, Visiter)',
      'Prestataires à découvrir (photo, nom, service, ville, note, Voir profil)',
      'Produits mis en avant (vendeurs sponsorisés)',
      'Services / produits proches (géolocalisation)',
      'Promotions',
      'Pourquoi utiliser Zifek (acheter local, trouver un pro, publier, contacter, créer son espace pro) + CTA « Créez votre boutique Zifek »',
      'Footer marketplace (À propos, Aide, Professionnels, Conditions, Confidentialité, Contact, réseaux)',
    ],
    tags: ['marketplace_search', 'marketplace_categories', 'featured_products', 'marketplace_products', 'marketplace_sellers', 'testimonials'],
    nav: ['Accueil', 'Nos produits', 'Nos services', 'Nos vendeurs'],
    defaultInstructions: 'Design de place de marché : recherche omniprésente, densité maîtrisée, cartes claires avec prix et localisation, mise en avant des vendeurs et catégories.',
  },
  {
    id: 'mode',
    label: 'Mode & couture',
    icon: 'ri-shirt-line',
    description: 'Atelier de couture, créateur de mode, retouches, prêt-à-porter sur mesure.',
    typecompte: 5,
    kind: 'simple',
    structure: [
      'Header (logo + navigation + rendez-vous)',
      'Hero éditorial (photo ou mannequin, nom, phrase, CTA)',
      'Collections',
      'Produits / créations',
      'Portfolio des créations',
      'Services de couture (sur-mesure, retouches, cours)',
      'Avis clients',
      'Contact / prise de rendez-vous',
      'Footer',
    ],
    tags: ['products', 'services', 'portfolio', 'gallery', 'testimonials', 'booking_form', 'contact_form'],
    nav: ['Accueil', 'Nos produits', 'Nos services'],
    defaultInstructions: 'Design éditorial mode : grandes images, typographie display affirmée, beaucoup de blanc, ambiance atelier chic.',
  },
  {
    id: 'restauration',
    label: 'Restauration',
    icon: 'ri-restaurant-2-line',
    description: 'Restaurant, snack, food truck, traiteur, pâtisserie.',
    typecompte: 2,
    kind: 'simple',
    structure: [
      'Header (logo + navigation + commande/réservation)',
      'Hero appétissant (plat signature, nom, phrase, CTA réserver/commander)',
      'Menu / carte',
      'Plats populaires',
      'Promotions',
      'Galerie (plats, ambiance, lieux)',
      'À propos (histoire, chef)',
      'Horaires',
      'Localisation (adresse, carte)',
      'Commande / réservation',
      'Footer',
    ],
    tags: ['products', 'services', 'gallery', 'testimonials', 'booking_form', 'contact_form'],
    nav: ['Accueil', 'Nos produits', 'Nos services'],
    defaultInstructions: 'Ambiance gourmande et chaleureuse : photos appétissantes, couleurs chaudes (rouge profond, ocre, crème), typographie conviviale.',
  },
  {
    id: 'mixte',
    label: 'Mixte (produits + services)',
    icon: 'ri-layout-grid-line',
    description: 'Commerce proposant à la fois des produits et des services (garage, salon, magasin + atelier…).',
    typecompte: 6,
    kind: 'simple',
    structure: [
      'Header (logo + navigation + produits + services)',
      'Hero (image, slogan, deux CTA : voir produits / réserver)',
      'Produits',
      'Services',
      'Promotions',
      'Réalisations',
      'Avis clients',
      'À propos',
      'Localisation',
      'Contact',
      'Footer',
    ],
    tags: ['products', 'services', 'portfolio', 'testimonials', 'stats', 'contact_form'],
    nav: ['Accueil', 'Nos produits', 'Nos services'],
    defaultInstructions: 'Équilibre produits / services : deux axes visibles dès le hero, sections alternées, hiérarchie claire.',
  },
];

export function getBlueprint(id: string | null | undefined): ThemeBlueprint | null {
  if (!id) return null;
  return THEME_BLUEPRINTS.find((b) => b.id === id) || null;
}

// ── Badge coloré par type de commerce ────────────────────────────────
// Couleurs issues des rôles du design system (jamais de bleu/violet).
export interface BlueprintBadge {
  label: string;
  icon: string;
  className: string;
}

const BLUEPRINT_BADGE_CLASS: Record<string, string> = {
  boutique: 'bg-primary-100 text-primary-700',
  services: 'bg-accent-100 text-accent-700',
  hotellerie: 'bg-secondary-100 text-secondary-700',
  marketplace: 'bg-foreground-100 text-foreground-700',
  mode: 'bg-accent-100 text-accent-800',
  restauration: 'bg-primary-100 text-primary-800',
  mixte: 'bg-secondary-100 text-secondary-800',
};

/** Badge (libellé + couleur) pour un type de commerce donné. */
export function getBlueprintBadge(id?: string | null): BlueprintBadge | null {
  const bp = getBlueprint(id);
  if (!bp) return null;
  return {
    label: bp.label,
    icon: bp.icon,
    className: BLUEPRINT_BADGE_CLASS[bp.id] || 'bg-background-100 text-foreground-700',
  };
}

/**
 * Devine un type de commerce à partir du nom de la catégorie (typetheme),
 * pour donner un badge aux thèmes plus anciens sans blueprint enregistré.
 */
export function inferBlueprintIdFromCategory(name?: string | null): string | null {
  if (!name) return null;
  const n = name.toLowerCase();
  if (/hotel|hôtel|riad|heberg|héberg|auberge|residence|résidence/.test(n)) return 'hotellerie';
  if (/market|place de march/.test(n)) return 'marketplace';
  if (/resto|restaut|restaurant|food|traiteur|patiss|pâtiss|snack/.test(n)) return 'restauration';
  if (/coutur|mode|mode|atelier|retouche|prêt|pret|fashion/.test(n)) return 'mode';
  if (/service|prestation|coiff|consult|photograph|mécanic|mecan|agence/.test(n)) return 'services';
  if (/mixte|produit.*service/.test(n)) return 'mixte';
  if (/boutique|vente|e-?commerce|commerce|shop|store|épicerie|epicerie|cosmét|cosmet|électron/.test(n)) return 'boutique';
  return null;
}

/**
 * Construit le bloc de prompt décrivant le blueprint à suivre.
 * Injecté dans le prompt système de l'IA pour garantir un thème
 * pertinent (bonnes sections, bons tags, bonne navigation).
 */
export function buildBlueprintPromptBlock(id: string | null | undefined): string {
  const bp = getBlueprint(id);
  if (!bp) return '';

  const structureList = bp.structure.map((s, i) => `${i + 1}. ${s}`).join('\n');
  const tagsList = bp.tags.join(', ');
  const navList = bp.nav.join(' | ');

  return `

🏗️ BLUEPRINT IMPOSÉ — TYPE DE COMMERCE : ${bp.label.toUpperCase()}
${bp.description}
${bp.kind === 'marketplace' ? '⚠️ Ce thème est une MARKETPLACE : il doit exposer les balises marketplace_* et permettre à plusieurs vendeurs de proposer leurs annonces.' : ''}

📐 STRUCTURE ATTENDUE (respecte cet ordre pour la page d'accueil) :
${structureList}

🧩 BALISES À PRIVILÉGIER POUR CE TYPE : ${tagsList}

🧭 NAVIGATION ATTENDUE (RÈGLE STRICTE) :
La barre de navigation contient UNIQUEMENT : le logo + ${navList} + panier + compte.
Ne génère aucun autre lien de navigation : le marchand ajoutera ses liens lui-même depuis son tableau de bord.

🎨 ORIENTATION DESIGN POUR CE TYPE :
${bp.defaultInstructions}
`;
}